/*
 * Insights (draft 10, FEATURE-MAP 11): the company's numbers from its own records (money collected, never what an ad
 * platform claims), the forecast, job costing, marketing costs by channel, the summary across companies, and Ask.
 */
import { z } from "zod";

const day = z.iso.date();

export const insightsQuery = z.object({ from: day.optional(), to: day.optional() });
export type InsightsQuery = z.infer<typeof insightsQuery>;

export const insightsMonth = z.object({
  month: z.string(),
  billedCents: z.number(),
  collectedCents: z.number(),
  refundsCents: z.number(),
  newClients: z.number(),
  visitsDone: z.number(),
});

export const channelRow = z.object({
  /** How they found us (the client's lead source), "Not set" when blank. */
  channel: z.string(),
  newClients: z.number(),
  collectedCents: z.number(),
  /** Marketing costs typed in for the channel in these months. */
  costCents: z.number(),
  costPerClientCents: z.number().nullable(),
});

export const jobCostRow = z.object({
  jobId: z.uuid().nullable(),
  name: z.string(),
  clientName: z.string(),
  visits: z.number(),
  billedCents: z.number(),
  laborCents: z.number(),
  profitCents: z.number(),
  /** Profit as a part of what came in, a whole percent; null when nothing came in. */
  marginPercent: z.number().nullable(),
  /** Visits with nobody set on them: no labor is counted for them. */
  noCrew: z.number(),
  /** The job repeats (weekly and the rest), not one-time. */
  recurring: z.boolean(),
});
export type JobCostRow = z.infer<typeof jobCostRow>;

export const insightsAnswer = z.object({
  from: z.string(),
  to: z.string(),
  money: z.object({
    billedCents: z.number(),
    collectedCents: z.number(),
    refundsCents: z.number(),
    /** Owed now on invoices sent and not paid, and the part of it past due. */
    openCents: z.number(),
    pastDueCents: z.number(),
  }),
  months: z.array(insightsMonth),
  clients: z.object({
    newClients: z.number(),
    repeatClients: z.number(),
    planClients: z.number(),
    lost: z.number(),
    lostReasons: z.array(z.object({ reason: z.string(), count: z.number() })),
    /** Lost in these days as a part of the clients there were, a whole percent. */
    churnPercent: z.number().nullable(),
  }),
  quotes: z.object({ sent: z.number(), won: z.number(), lost: z.number(), winPercent: z.number().nullable() }),
  channels: z.array(channelRow),
  jobCosts: z.array(jobCostRow),
  payments: z.object({
    byMethod: z.array(z.object({ method: z.string(), cents: z.number(), count: z.number() })),
    failed: z.number(),
  }),
  /** What the charts draw (Jobber's Insights page). */
  charts: z.object({
    /** The same number of days just before, for the up and down arrows. */
    previous: z.object({
      from: z.string(),
      to: z.string(),
      billedCents: z.number(),
      collectedCents: z.number(),
      newClients: z.number(),
      visitsDone: z.number(),
      quotesSent: z.number(),
      quotesWon: z.number(),
      leads: z.number(),
    }),
    /** Money collected each month from January last year to now, for last year next to this year. */
    years: z.array(z.object({ month: z.string(), collectedCents: z.number() })),
    /** Leads that came in these days, how many got a quote sent, how many said yes. */
    leads: z.object({ created: z.number(), quoted: z.number(), won: z.number() }),
    /** Quotes sent in these days by the Monday of their week. */
    quoteWeeks: z.array(
      z.object({ week: z.string(), sent: z.number(), sentCents: z.number(), won: z.number(), wonCents: z.number() }),
    ),
    /** Who owes on invoices sent and not paid, most owed first. */
    owing: z.object({
      clients: z.number(),
      top: z.array(z.object({ clientId: z.uuid(), name: z.string(), balanceCents: z.number(), lateCents: z.number() })),
    }),
    /** Average whole days from first sending an invoice to it being paid, for invoices paid in these days. */
    payDays: z.number().nullable(),
  }),
});
export type InsightsAnswer = z.infer<typeof insightsAnswer>;

export const forecastWindow = z.object({
  days: z.number(),
  visits: z.number(),
  bookedCents: z.number(),
  invoicesCents: z.number(),
  payrollCents: z.number(),
  netCents: z.number(),
});
export const forecastAnswer = z.object({
  windows: z.array(forecastWindow),
  /** Booked work in each of the next 8 weeks, starting today. */
  weeks: z.array(z.object({ start: z.string(), visits: z.number(), bookedCents: z.number() })),
  quotes: z.object({ openCents: z.number(), winPercent: z.number().nullable(), likelyCents: z.number().nullable() }),
  /** runs: the past 8 weeks of paid payroll; share: the labor share of recent work; none: nothing to go on yet. */
  payrollHow: z.enum(["runs", "share", "none"]),
});
export type ForecastAnswer = z.infer<typeof forecastAnswer>;

export const marketingCostInput = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Pick a month."),
  channel: z.string().trim().min(1).max(40),
  amountCents: z.number().int().min(0).max(100_000_000),
  note: z.string().trim().max(200).optional(),
});
export const marketingCostRow = marketingCostInput.extend({ id: z.uuid(), note: z.string().nullable() });
export type MarketingCostRow = z.infer<typeof marketingCostRow>;

export const companyStatsRow = z.object({
  companyId: z.uuid(),
  name: z.string(),
  billedCents: z.number(),
  collectedCents: z.number(),
  newClients: z.number(),
  visitsDone: z.number(),
});
export type CompanyStatsRow = z.infer<typeof companyStatsRow>;

export const askInput = z.object({ question: z.string().trim().min(3).max(500) });
export const askAnswer = z.object({ id: z.uuid(), question: z.string(), answer: z.string(), at: z.string() });
export type AskAnswer = z.infer<typeof askAnswer>;
